// src/pages/Attendance/LeaveBalancePage.tsx

import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {  RootState } from '../../store';
import {
  Calendar,
  HeartPulse,
  Coffee,
  Zap,
  Briefcase,
  TrendingUp,
} from 'lucide-react';

interface LeaveBalancePageProps {
  onLeaveTypeClick?: (type: string) => void;
}

const LeaveBalancePage: React.FC<LeaveBalancePageProps> = ({ onLeaveTypeClick }) => {
  const leaveBalances = useSelector((state: RootState) => state.leave.balances);
  const loading = useSelector((state: RootState) => state.leave.loading);

    const leaveTypeToLabel: Record<string, string> = {
    paid: "Paid Leave",
    sick: "Sick Leave",
    compOff: "Comp-Off",
    casual: "Casual Leave",
    lossOfPay: "Loss of Pay",
    paternity: "Paternity Leave"
  }
  
  const styleMap: Record<
    string,
    { name: string; color: string; icon: JSX.Element; unit: string }
  > = {
    paid: {
      name: "Paid Leave",
      color: "bg-gradient-to-br from-blue-500 to-indigo-600",
      icon: <Calendar className="w-5 h-5 text-white" />,
      unit: "days",
    },
    sick: {
      name: "Sick Leave",
      color: "bg-gradient-to-br from-emerald-500 to-teal-600",
      icon: <HeartPulse className="w-5 h-5 text-white" />,
      unit: "days",
    },
    compOff: {
      name: "Compensatory Off",
      color: "bg-gradient-to-br from-purple-500 to-fuchsia-600",
      icon: <Zap className="w-5 h-5 text-white" />,
      unit: "days",
    },
    casual: {
      name: "Casual Leave",
      color: "bg-gradient-to-br from-amber-500 to-orange-500",
      icon: <Coffee className="w-5 h-5 text-white" />,
      unit: "days",
    },
    paternity: {
      name: "Paternity Leave",
      color: "bg-gradient-to-br from-rose-400 to-pink-500",
      icon: <Briefcase className="w-5 h-5 text-white" />,
      unit: "days",
    },
    lossOfPay: {
      name: "Loss of Pay",
      color: "bg-gradient-to-br from-red-400 to-red-600",
      icon: <Briefcase className="w-5 h-5 text-white" />,
      unit: "days",
    },
  };

  const fallbackStyle = {
    name: "Other Leave",
    color: "bg-gradient-to-br from-cyan-500 to-blue-500",
    icon: <Calendar className="w-5 h-5 text-white" />,
    unit: "days",
  };

const handleCardClick = (leaveTypeKey: string) => {
  const label = leaveTypeToLabel[leaveTypeKey] || 
                (styleMap[leaveTypeKey]?.name || leaveTypeKey);
  
  localStorage.setItem("preSelectedLeaveType", JSON.stringify({
    key: leaveTypeKey,
    label
  }));
  
  if (onLeaveTypeClick) onLeaveTypeClick(label);
};


  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <div className="animate-pulse flex space-x-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex-1 h-32 bg-gray-200 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Leave Balance</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {Object.entries(leaveBalances).map(([key, value]) => {
          const style = styleMap[key] || {
            ...fallbackStyle,
            name: key
              .replace(/([A-Z])/g, " $1")
              .replace(/^./, (s) => s.toUpperCase()),
          };

          return (
            <div
              key={key}
              onClick={() => handleCardClick(key)}
              className="cursor-pointer rounded-xl shadow-sm overflow-hidden border border-gray-100 transition-all hover:shadow-md hover:-translate-y-1"
            >
              <div className={`${style.color} p-4 flex justify-between items-center`}>
                <div className="p-2 rounded-lg bg-white/20 backdrop-blur-sm">
                  {style.icon}
                </div>
                <span className="text-xs font-medium text-white/90 uppercase tracking-wider">
                  {style.name}
                </span>
              </div>

              <div className="p-5 bg-white">
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-3xl font-bold text-gray-800">{value}</p>
                    <p className="text-sm text-gray-500 mt-1">{style.unit} available</p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LeaveBalancePage;